/**
 * Los datos legales de la explotación (tabla `farm_legal_profiles`, CQ1): lo que
 * el cuaderno pregunta UNA VEZ por empresa (PLAN §3) y el formulario «Datos de la
 * explotación» de Ajustes → Empresa escribe.
 *
 * Aquí vive lo que no es pantalla:
 *   - el formulario como datos (`DatosLegalesForm`) y su paso de/a la fila;
 *   - la validación, con los validadores del cuaderno (NIF con su control, ROPO,
 *     código postal) y devolviendo CLAVES de i18n, no textos;
 *   - `per_location` (captaciones y zonas específicas del 2.2 por finca) con zod,
 *     porque en la base es un jsonb sin esquema.
 *
 * D14: el cuaderno es por titular. Una fila con `location_id` NULL es la de toda
 * la empresa; una finca de OTRO titular lleva la suya.
 * D18: el asesor solo cuenta con nº ROPO de asesor: sin él no se guarda.
 * E11: el NIF no se escribe en ningún log; los errores dicen QUÉ campo, nunca su valor.
 */
import { z } from "zod";
import { checkNif, checkRopo, isValidCodigoPostal } from "./validators";

export const GIP_TIPOS = ["AE", "PI", "CP", "Atrias", "AS", "NO"] as const;
export const RIEGOS = ["SEC", "ASP", "LOC", "GRA"] as const;
export const PROTECCIONES = ["AL", "M", "BP", "INV"] as const;
export const SISTEMAS_RIEGO = [1, 2, 3, 4, 5, 6, 7, 8] as const;

export type GipTipo = (typeof GIP_TIPOS)[number];
export type Riego = (typeof RIEGOS)[number];
export type Proteccion = (typeof PROTECCIONES)[number];

/** Lo del 2.2 que va por finca. «no» / «total» / «parcial» para la zona específica. */
export const DatosFinca = z.object({
  captacion: z.boolean().default(false),
  captacion_distancia_m: z.number().finite().min(0).nullish(),
  captacion_denominacion: z.string().trim().max(200).nullish(),
  captacion_utm: z.string().trim().max(80).nullish(),
  zona: z.enum(["no", "total", "parcial"]).default("no"),
  zona_ha: z.number().finite().min(0).nullish(),
});
export type DatosFinca = z.infer<typeof DatosFinca>;

/** El jsonb entero: id de finca → sus datos. Lo que no cuadra se descarta, no revienta. */
export function parsePerLocation(raw: unknown): Record<string, DatosFinca> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, DatosFinca> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const r = DatosFinca.safeParse(v);
    if (r.success) out[k] = r.data;
  }
  return out;
}

/** Los campos de texto del formulario, en el orden en que se enseñan. */
export const CAMPOS_EXPLOTACION = [
  "razon_social", "nif", "rea_nacional", "reafa", "direccion", "localidad", "codigo_postal", "provincia",
  "telefono_fijo", "telefono_movil", "email",
] as const;
export const CAMPOS_TITULAR = [
  "titular_nombre", "titular_nif", "representacion_tipo", "titular_direccion", "titular_localidad",
  "titular_codigo_postal", "titular_provincia", "titular_telefono", "titular_email",
] as const;
export const CAMPOS_ASESOR = ["asesor_nombre", "asesor_nif", "asesor_ropo"] as const;

type CampoTexto = (typeof CAMPOS_EXPLOTACION)[number] | (typeof CAMPOS_TITULAR)[number] | (typeof CAMPOS_ASESOR)[number];

export type DatosLegalesForm = Record<CampoTexto, string> & {
  gip_tipo: GipTipo | "";
  riego_defecto: Riego | "";
  sistema_riego_defecto: string;
  proteccion_defecto: Proteccion | "";
  per_location: Record<string, DatosFinca>;
};

/** La fila tal y como llega de la base (lo mínimo que se lee). */
export type DatosLegalesRow = Partial<Record<CampoTexto, string | null>> & {
  gip_tipo?: string | null;
  riego_defecto?: string | null;
  sistema_riego_defecto?: number | null;
  proteccion_defecto?: string | null;
  per_location?: unknown;
};

/** Lo que se sabe de la empresa ANTES de tener fila: para no hacer escribir dos veces el nombre. */
export interface EmpresaBase {
  name?: string | null;
  address?: string | null;
  city?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
}

const TODOS_TEXTO: CampoTexto[] = [...CAMPOS_EXPLOTACION, ...CAMPOS_TITULAR, ...CAMPOS_ASESOR];

const enLista = <T extends string>(lista: readonly T[], v: string | null | undefined): T | "" =>
  (lista as readonly string[]).includes(v ?? "") ? (v as T) : "";

/**
 * La fila → el formulario. Sin fila (primera vez), se rellenan con lo que la
 * empresa ya tiene en `companies` —nombre, dirección, ciudad, correo, teléfono—
 * y los valores por defecto de invernadero (goteo, sistema 6, invernadero), que
 * el formulario enseña y el admin cambia. Nada se guarda hasta que pulsa Guardar.
 */
export function formFromRow(row: DatosLegalesRow | null | undefined, empresa?: EmpresaBase | null): DatosLegalesForm {
  const form = Object.fromEntries(TODOS_TEXTO.map((k) => [k, row?.[k] ?? ""])) as Record<CampoTexto, string>;
  if (!row && empresa) {
    form.razon_social = empresa.name ?? "";
    form.direccion = empresa.address ?? "";
    form.localidad = empresa.city ?? "";
    form.email = empresa.contact_email ?? "";
    form.telefono_movil = empresa.contact_phone ?? "";
  }
  return {
    ...form,
    gip_tipo: enLista(GIP_TIPOS, row?.gip_tipo),
    riego_defecto: row ? enLista(RIEGOS, row.riego_defecto) : "LOC",
    sistema_riego_defecto: row ? (row.sistema_riego_defecto ? String(row.sistema_riego_defecto) : "") : "6",
    proteccion_defecto: row ? enLista(PROTECCIONES, row.proteccion_defecto) : "INV",
    per_location: parsePerLocation(row?.per_location),
  };
}

/** El formulario → lo que se escribe. Vacío es NULL; los NIF, limpios. */
export function rowFromForm(form: DatosLegalesForm): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of TODOS_TEXTO) {
    const v = (form[k] ?? "").trim();
    out[k] = v === "" ? null : v;
  }
  for (const k of ["nif", "titular_nif", "asesor_nif"] as const) {
    if (out[k]) out[k] = checkNif(out[k] as string).value;
  }
  if (out.asesor_ropo) out.asesor_ropo = checkRopo(out.asesor_ropo as string).value;
  out.gip_tipo = form.gip_tipo || null;
  out.riego_defecto = form.riego_defecto || null;
  out.sistema_riego_defecto = form.sistema_riego_defecto ? Number(form.sistema_riego_defecto) : null;
  out.proteccion_defecto = form.proteccion_defecto || null;
  out.per_location = form.per_location ?? {};
  return out;
}

/** Campo → clave i18n del error (espacio `cuadernoDatos.errors`). */
export type DatosLegalesErrors = Partial<Record<CampoTexto, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Qué está MAL (no qué falta: se puede guardar a medias y el cuaderno cuenta los
 * huecos). Un NIF con la letra equivocada, un ROPO que no lo parece o un código
 * postal imposible no se guardan: con aspecto de buenos, se copiarían tal cual.
 */
export function validarDatosLegales(form: DatosLegalesForm): DatosLegalesErrors {
  const errors: DatosLegalesErrors = {};
  for (const k of ["nif", "titular_nif", "asesor_nif"] as const) {
    const v = (form[k] ?? "").trim();
    if (!v) continue;
    const r = checkNif(v);
    // `"reason" in r` y no `!r.ok`: sin strictNullChecks, TS no estrecha la unión por un booleano.
    if ("reason" in r) errors[k] = r.reason === "control" ? "cuadernoDatos.errors.nifControl" : "cuadernoDatos.errors.nifFormat";
  }
  for (const k of ["codigo_postal", "titular_codigo_postal"] as const) {
    const v = (form[k] ?? "").trim();
    if (v && !isValidCodigoPostal(v)) errors[k] = "cuadernoDatos.errors.postalCode";
  }
  for (const k of ["email", "titular_email"] as const) {
    const v = (form[k] ?? "").trim();
    if (v && !EMAIL_RE.test(v)) errors[k] = "cuadernoDatos.errors.email";
  }
  const ropo = (form.asesor_ropo ?? "").trim();
  if (ropo && !checkRopo(ropo).ok) errors.asesor_ropo = "cuadernoDatos.errors.ropo";
  // D18: un asesor sin nº ROPO de asesor no es un asesor para el cuaderno.
  if ((form.asesor_nombre ?? "").trim() && !ropo) errors.asesor_ropo = "cuadernoDatos.errors.ropoRequired";
  return errors;
}

/** ¿Cuántos de los datos que el cuaderno pide una vez faltan? (para la tarjeta). */
export function camposQueFaltan(row: DatosLegalesRow | null | undefined): CampoTexto[] {
  const imprescindibles: CampoTexto[] = ["razon_social", "nif", "direccion", "localidad", "codigo_postal", "provincia", "titular_nombre", "titular_nif"];
  return imprescindibles.filter((k) => !(row?.[k] ?? "").toString().trim());
}
